import { BasicProfile, DEFAULT_PROFILE } from "protico-sdk";
import useSWR from "swr";
import { useAppSelector } from ".";
import { getProticoClient } from "../utils/api";
import { checkAddress, getImgproxyUrl } from "../utils/format.utils";
import { fixOldInfuraUrl } from "../utils/ipfs.utils";
import { useWeb3Auth } from "./useWeb3Auth";
import { AIAvatarData, AIAvatarV2Data } from "../types/types";
import { useTranslation } from "react-i18next";

export const getStableBasicProfile = async (
  targetAddress: string,
  currentLanguage?: string,
  isUser?: boolean
) => {
  try {
    checkAddress(targetAddress);
  } catch (_) {
    return { ...DEFAULT_PROFILE, aiavatar: null as AIAvatarV2Data | null };
  }
  // currentLanguage 是 "zh-TW" 或 "en-US"
  let name: string;
  let basicProfile: BasicProfile | undefined;
  let aiavatar: AIAvatarV2Data | null;

  // 1. 先拿到 basicProfile
  if (isUser) {
    basicProfile = await getProticoClient().profile.get(
      `${targetAddress}?time=${Date.now()}`
    );
  } else {
    basicProfile = await getProticoClient().profile.get(targetAddress);
  }

  // 2. 選 name
  if (basicProfile) {
    // 中文時優先用 zh_name，英文時優先用 en_name，若都沒有再退到 name 欄位，最後才是 generateName
    if (currentLanguage === "zh-TW") {
      name =
        basicProfile.zh_name ??
        basicProfile.name ??
        generateName(isUser, targetAddress);
    } else {
      // 預設視為英文
      name =
        basicProfile.en_name ??
        basicProfile.name ??
        generateName(isUser, targetAddress);
        console.log("en name: ", name);
    }
  } else {
    // profile 拿不到時，用 DEFAULT_PROFILE 走同樣邏輯
    basicProfile = DEFAULT_PROFILE;
    if (currentLanguage === "zh-TW") {
      name = DEFAULT_PROFILE.zh_name ?? generateName(isUser, targetAddress);
    } else {
      name = DEFAULT_PROFILE.en_name ?? generateName(isUser, targetAddress);
    }
  }

  let avatar_url = fixOldInfuraUrl(basicProfile?.avatar_url);
  if (avatar_url) {
    avatar_url = getImgproxyUrl(avatar_url);
  }

  try {
    const res = await getProticoClient().api.get(
      "/api/v1/avatar-system/avatar-v2/registry/user/" + targetAddress
    );
    if (!res.data.id) {
      aiavatar = null;
    } else {
      aiavatar = res.data;
    }
  } catch (err) {
    aiavatar = null;
  }

  return {
    ...basicProfile,
    name,
    avatar_url,
    aiavatar,
  };
};

const fetcher = async (
  targetAddress: string,
  currentAddress: string,
  currentLanguage: string
) => {
  return await getStableBasicProfile(
    targetAddress,
    currentLanguage,
    targetAddress === currentAddress
  );
};
const useBasicProfile = (address: string) => {
  const { address: currentAddress } = useWeb3Auth();
  const { i18n } = useTranslation();
  const currentLanguage =
    !!i18n.language && i18n.language.length > 0 && i18n.language !== "en-US"
      ? i18n.language
      : "zh-TW";
  const { data, error, mutate } = useSWR(
    [address, currentAddress, currentLanguage, "useBasicProfile"],
    fetcher,
    {
      refreshInterval: 60000,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    isPending: !error && !data,
    basicProfile: data ?? {
      ...DEFAULT_PROFILE,
      aiavatar: null as AIAvatarV2Data | null,
    },
    isError: error,
    refetch: mutate,
  };
};
export default useBasicProfile;

function generateName(isUser: boolean, targetAddress: string) {
  return isUser
    ? ""
    : `${targetAddress.slice(0, 8)}...${targetAddress.slice(-3)}`;
}
